import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import styles from './Ibtikar.module.css';
import axios from 'axios';
import { API } from '../../../config/API';
import Loader from '../../../components/Loader/Loader';
import ReactECharts from 'echarts-for-react';

const Leader = () => {
  const [data, setData] = useState([]);
  const [fullData, setFullData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [projectChartData, setProjectChartData] = useState([]);
  const [levelChartData, setLevelChartData] = useState([]);
  const [aggregateProjectChart, setAggregateProjectChart] = useState(null);
  const [aggregateLevelChart, setAggregateLevelChart] = useState(null);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const [showLevelDetails, setShowLevelDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('project');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [loader, setLoader] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
    listIdeas();
  }, []);

  useEffect(() => {
    if (departments.length > 0 && fullData.length > 0 && users.length > 0) {
      processChartData(fullData);
      processProjectChartData(fullData);
      processLevelChartData(fullData);
      processAggregateProjectChart(fullData);
      processAggregateLevelChart(fullData);
    }
  }, [departments, fullData, users]);

  useEffect(() => {
    filterData();
  }, [selectedDepartment, selectedStatus]);

  const fetchDepartments = async () => {
    try {
      const resp = await axios.get(API.getDepartmentData);
      if (resp && resp.status === 200) {
        console.log('Departments fetched:', resp.data);
        setDepartments(resp.data || []);
      }
    } catch (er) {
      console.log(er);
    }
  };

  const fetchUsers = async () => {
    try {
      const resp = await axios.get(API.listUser);
      if (resp && resp.status === 200 && resp.data) {
        // Exclude super admin users
        const filteredUsers = resp.data.users.filter(user => user.role !== 'SUPER_ADMIN');
        setUsers(filteredUsers);
      }
    } catch (er) {
      console.log(er);
    }
  };

  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'NA';
    
    // If departmentId is an object with a name, use it directly
    if (typeof departmentId === 'object' && departmentId.name) {
      return departmentId.name;
    }
    
    // Extract numeric ID if it's an object with id property, otherwise use as-is
    const deptId = typeof departmentId === 'object' ? departmentId.id : departmentId;
    if (!deptId) return 'NA';
    
    // Look up in departments array
    const dept = departments.find(d => Number(d.id) === Number(deptId));
    return dept ? dept.name : 'NA';
  };

  const getUsername = (id) => {
    if (id === undefined || id === null) return 'NA';
    const user = users.find(u => Number(u.id) === Number(id));
    if (!user) return 'NA';
    const username = user.username || '';
    const name = user.name || '';
    if (username && name) return `${username} - ${name}`;
    return username || name || 'NA';
  };

  const getSubmittedBy = (empId) => {
    if (!empId) return 'NA';
    const user = users.find(u => u.username === empId || u.name === empId);
    if (!user) return empId; // fallback: show raw empId if user not found
    const username = user.username || '';
    const name = user.name || '';
    if (username && name) return `${username} - ${name}`;
    return username || name || empId;
  };

  const listIdeas = async () => {
    try {
      setLoader(true);
      const resp = await axios.get(API.ideaList);
      if (resp && resp.status === 200 && resp.data && resp.data.status === "S") {
        const ideas = resp.data.ideas || [];
        console.log('Ideas fetched:', ideas);
        console.log('Sample idea departmentId:', ideas[0]?.departmentId);
        setData(ideas);
        setFullData(ideas);
        processChartData(ideas);
      }
    } catch (er) {
      console.log(er);
    } finally {
      setLoader(false);
    }
  };

  const processChartData = (ideas) => {
    // Group ideas by department and status
    const deptStatusMap = {};
    
    ideas.forEach(idea => {
      // Handle if departmentId is an object with id or name property
      let deptId, deptName;
      if (typeof idea.departmentId === 'object' && idea.departmentId) {
        deptId = idea.departmentId.id;
        deptName = idea.departmentId.name;
      } else {
        deptId = idea.departmentId;
        deptName = null;
      }
      
      if (!deptId) return; // Skip ideas without department ID
      const status = idea.status || 'Unknown';
      
      if (!deptStatusMap[deptId]) {
        deptStatusMap[deptId] = {};
      }
      
      if (!deptStatusMap[deptId][status]) {
        deptStatusMap[deptId][status] = 0;
      }
      
      deptStatusMap[deptId][status]++;
    });

    // Convert to echarts format
    const charts = [];
    
    Object.keys(deptStatusMap).forEach(deptId => {
      // Skip if deptId is undefined or 'undefined' string
      if (!deptId || deptId === 'undefined') return;
      
      // Try to find department name from departments array
      const department = departments.find(d => Number(d.id) === Number(deptId));
      // Also check if any idea has this deptId as object with name
      const ideaWithDept = ideas.find(i => {
        if (typeof i.departmentId === 'object' && i.departmentId) {
          return String(i.departmentId.id) === String(deptId) && i.departmentId.name;
        }
        return false;
      });
      const deptName = department ? department.name : 
                       (ideaWithDept?.departmentId?.name || `Department ${deptId}`);
      
      console.log(`Processing deptId: ${deptId}, found name: ${deptName}, department object:`, department);
      
      const statusData = Object.keys(deptStatusMap[deptId]).map(status => ({
        name: status,
        value: deptStatusMap[deptId][status],
        itemStyle: {
          color: getStatusColor(status)
        }
      }));

      const total = Object.values(deptStatusMap[deptId]).reduce((sum, count) => sum + count, 0);

      // ECharts option
      const chartOption = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          textStyle: {
            fontSize: 12
          }
        },
        series: [
          {
            name: deptName,
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '60%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: statusData
          }
        ]
      };

      charts.push({
        departmentName: deptName,
        option: chartOption,
        total: total
      });
    });

    setChartData(charts);
  };

  const processProjectChartData = (ideas) => {
    // Group ideas by project and status
    const projectStatusMap = {};
    
    ideas.forEach(idea => {
      // Find user by empId (username)
      const user = users.find(u => u.username === idea.empId || u.name === idea.empId);
      const projectName = user && user.projectName ? user.projectName : 'Unassigned';
      const status = idea.status || 'Unknown';
      
      if (!projectStatusMap[projectName]) {
        projectStatusMap[projectName] = {};
      }
      
      if (!projectStatusMap[projectName][status]) {
        projectStatusMap[projectName][status] = 0;
      }
      
      projectStatusMap[projectName][status]++;
    });

    // Convert to echarts format
    const charts = [];
    
    Object.keys(projectStatusMap).forEach(projectName => {
      const statusData = Object.keys(projectStatusMap[projectName]).map(status => ({
        name: status,
        value: projectStatusMap[projectName][status],
        itemStyle: {
          color: getStatusColor(status)
        }
      }));

      const total = Object.values(projectStatusMap[projectName]).reduce((sum, count) => sum + count, 0);

      const chartOption = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          textStyle: {
            fontSize: 12
          }
        },
        series: [
          {
            name: projectName,
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '60%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: statusData
          }
        ]
      };

      charts.push({
        name: projectName,
        option: chartOption,
        total: total
      });
    });

    setProjectChartData(charts);
  };

  const processLevelChartData = (ideas) => {
    // Group ideas by level and status
    const levelStatusMap = {};
    
    ideas.forEach(idea => {
      // Find user by empId (username)
      const user = users.find(u => u.username === idea.empId || u.name === idea.empId);
      const levelName = user && user.levelName ? user.levelName : 'Unassigned';
      const status = idea.status || 'Unknown';
      
      if (!levelStatusMap[levelName]) {
        levelStatusMap[levelName] = {};
      }
      
      if (!levelStatusMap[levelName][status]) {
        levelStatusMap[levelName][status] = 0;
      }
      
      levelStatusMap[levelName][status]++;
    });

    // Convert to echarts format
    const charts = [];
    
    Object.keys(levelStatusMap).forEach(levelName => {
      const statusData = Object.keys(levelStatusMap[levelName]).map(status => ({
        name: status,
        value: levelStatusMap[levelName][status],
        itemStyle: {
          color: getStatusColor(status)
        }
      }));

      const total = Object.values(levelStatusMap[levelName]).reduce((sum, count) => sum + count, 0);

      const chartOption = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          textStyle: {
            fontSize: 12
          }
        },
        series: [
          {
            name: levelName,
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['50%', '60%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: statusData
          }
        ]
      };

      charts.push({
        name: levelName,
        option: chartOption,
        total: total
      });
    });

    setLevelChartData(charts);
  };

  const processAggregateProjectChart = (ideas) => {
    // Group all ideas by project
    const projectCounts = {};
    
    ideas.forEach(idea => {
      // Find user by empId (username)
      const user = users.find(u => u.username === idea.empId || u.name === idea.empId);
      const projectName = user && user.projectName ? user.projectName : 'Unassigned';
      
      if (!projectCounts[projectName]) {
        projectCounts[projectName] = 0;
      }
      
      projectCounts[projectName]++;
    });

    // Generate colors for each project
    const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
    
    const projectData = Object.keys(projectCounts).map((projectName, index) => ({
      name: projectName,
      value: projectCounts[projectName],
      itemStyle: {
        color: colors[index % colors.length]
      }
    }));

    const total = Object.values(projectCounts).reduce((sum, count) => sum + count, 0);

    const chartOption = {
      title: {
        text: 'Project-wise Distribution',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ideas ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'middle',
        textStyle: {
          fontSize: 12
        }
      },
      series: [
        {
          name: 'Projects',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['60%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}: {d}%'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '16',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: true
          },
          data: projectData
        }
      ]
    };

    setAggregateProjectChart({ option: chartOption, total });
  };

  const processAggregateLevelChart = (ideas) => {
    // Group all ideas by level
    const levelCounts = {};
    
    ideas.forEach(idea => {
      // Find user by empId (username)
      const user = users.find(u => u.username === idea.empId || u.name === idea.empId);
      const levelName = user && user.levelName ? user.levelName : 'Unassigned';
      
      if (!levelCounts[levelName]) {
        levelCounts[levelName] = 0;
      }
      
      levelCounts[levelName]++;
    });

    // Generate colors for each level
    const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];
    
    const levelData = Object.keys(levelCounts).map((levelName, index) => ({
      name: levelName,
      value: levelCounts[levelName],
      itemStyle: {
        color: colors[index % colors.length]
      }
    }));

    const total = Object.values(levelCounts).reduce((sum, count) => sum + count, 0);

    const chartOption = {
      title: {
        text: 'Level-wise Distribution',
        left: 'center',
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ideas ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        top: 'middle',
        textStyle: {
          fontSize: 12
        }
      },
      series: [
        {
          name: 'Levels',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['60%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            position: 'outside',
            formatter: '{b}: {d}%'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '16',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: true
          },
          data: levelData
        }
      ]
    };

    setAggregateLevelChart({ option: chartOption, total });
  };

  const getStatusColor = (status) => {
    if (!status) return '#cccccc';
    const s = String(status).toLowerCase();
    if (s.includes('completed')) return '#28a745';
    if (s.includes('rejected')) return '#dc3545';
    if (s.includes('open')) return '#007bff';
    if (s.includes('implementation')) return '#fd7e14';
    if (s.includes('review')) return '#ffc107';
    return '#6c757d';
  };

  const filterData = () => {
    let filtered = [...fullData];
    
    // Filter by search term (title)
    if (selectedDepartment || selectedStatus) {
      // Apply department filter
      if (selectedDepartment) {
        filtered = filtered.filter(item => {
          const deptId = typeof item.departmentId === 'object' ? item.departmentId.id : item.departmentId;
          return deptId && Number(deptId) === Number(selectedDepartment);
        });
      }
      
      // Apply status filter
      if (selectedStatus) {
        filtered = filtered.filter(item => 
          item.status && item.status.toLowerCase() === selectedStatus.toLowerCase()
        );
      }
    }
    
    setData(filtered);
  };

  const handleSearch = (e) => {
    const key = e.target.value || '';
    let filtered = [...fullData];
    
    // Apply search filter
    if (key) {
      filtered = filtered.filter(item => 
        item.ideaProblem && item.ideaProblem.toLowerCase().includes(key.toLowerCase())
      );
    }
    
    // Apply department filter
    if (selectedDepartment) {
      filtered = filtered.filter(item => {
        const deptId = typeof item.departmentId === 'object' ? item.departmentId.id : item.departmentId;
        return deptId && Number(deptId) === Number(selectedDepartment);
      });
    }
    
    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(item => 
        item.status && item.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }
    
    setData(filtered);
  };

  const clearFilters = () => {
    setSelectedDepartment('');
    setSelectedStatus('');
    setData(fullData);
  };

  const getUniqueStatuses = () => {
    const statuses = [...new Set(fullData.map(item => item.status).filter(Boolean))];
    return statuses.sort();
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    const s = String(status).toLowerCase();
    if (s.includes('completed')) return styles.statusApproved;
    if (s.includes('rejected')) return styles.statusRejected;
    if (s.includes('open')) return styles.statusOpen;
    if (s.includes('implementation')) return styles.statusImplementation;
    if (s.includes('review')) return styles.statusReview;
    return '';
  };

  const columns = [
    { name: 'ID', selector: row => row.id, sortable: true, center: true, grow: 0.5 },
    {
      name: 'TITLE',
      selector: row => row.ideaProblem,
      sortable: true,
      center: true,
      grow: 3,
      cell: row => (
        <span className={styles.tableEllipsis} title={row.ideaProblem}>
          {row.ideaProblem}
        </span>
      ),
      minWidth: '120px',
      maxWidth: '240px',
    },
    { name: 'DATE', selector: row => row.ideaDate, center: true, maxWidth: '90px' },
    { name: 'DEPARTMENT', selector: row => getDepartmentName(row.departmentId), center: true },
    {
      name: 'SUBMITTED BY',
      selector: row => getSubmittedBy(row.empId),
      center: true,
      cell: row => {
        const label = getSubmittedBy(row.empId);
        return <span className={styles.tableEllipsis} title={label}>{label}</span>;
      },
    },
    {
      name: 'ASSIGNEE',
      selector: row => getUsername(row.assignee),
      center: true,
      cell: row => {
        const label = getUsername(row.assignee);
        return <span className={styles.tableEllipsis} title={label}>{label}</span>;
      },
    },
    { name: 'STATUS', selector: row => (row && row.status ? String(row.status).toUpperCase() : ''), center: true, maxWidth: '130px' },
    {
      name: '',
      cell: row => (
        <button
          className='btn btn-info'
          onClick={() => { setModalData(row); setModalOpen(true); }}
        >
          VIEW
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <>
      <div className={styles.container || ''} data-testid="training-leader">
        <div className='bread-crumb overflow-auto'>
          <div>
            <span className='breadcrumb-title'>
              <input type='button' className='btn' value='Home - Ibtikar - Leader' />
            </span>
          </div>
        </div>

        <div className='data-table-holder'>
          <div className='card p-4'>
            {/* Charts Section */}
            {chartData.length > 0 && (
              <div className="border rounded p-1 mb-1" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="chart-head mb-1">
                  <h5 style={{ background: 'none', color: '#333', margin: '0' }}>DEPARTMENT OVERVIEW - IDEA STATUS BREAKDOWN</h5>
                </div>
                <div className="row">
                  {chartData.map((chart, index) => (
                    <div key={index} className="col-md-4 mb-1">
                      <div className="chart-box" style={{ height: '320px', marginBottom: '1rem' }}>
                        <div style={{ height: '250px', width: '100%' }}>
                          <ReactECharts 
                            option={chart.option}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={true}
                            lazyUpdate={true}
                          />
                        </div>
                        <div className="text-center mt-1">
                          <h6 className="mb-1">{chart.departmentName}</h6>
                          <small>Total Ideas: {chart.total}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Aggregate Project and Level Overview Section */}
            {(aggregateProjectChart || aggregateLevelChart) && (
              <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="chart-head mb-3">
                  <h5 style={{ background: 'none', color: '#333', margin: '0', fontSize: '18px', fontWeight: 'bold' }}>
                    PROJECT & LEVEL OVERVIEW
                  </h5>
                </div>
                <div className="row">
                  {/* Project Distribution Chart */}
                  {aggregateProjectChart && (
                    <div className="col-md-6 mb-3">
                      <div className="chart-box" style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <div className="text-center mb-2">
                          <small style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>Total Ideas: {aggregateProjectChart.total}</small>
                        </div>
                        <div style={{ height: '320px', width: '100%' }}>
                          <ReactECharts 
                            option={aggregateProjectChart.option}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={true}
                            lazyUpdate={true}
                          />
                        </div>
                        <div className="text-center mt-2">
                          <button 
                            className="btn btn-sm" 
                            style={{ backgroundColor: '#F15c2a', color: '#fff', padding: '6px 20px', border: 'none', borderRadius: '4px', fontWeight: '500' }}
                            onClick={() => {
                              if (showProjectDetails) {
                                // Hide details
                                setShowProjectDetails(false);
                                setShowLevelDetails(false);
                              } else {
                                // Show project details only
                                setShowProjectDetails(true);
                                setShowLevelDetails(false);
                                setActiveTab('project');
                              }
                            }}
                          >
                            {showProjectDetails ? 'Hide Details' : 'View Details'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Level Distribution Chart */}
                  {aggregateLevelChart && (
                    <div className="col-md-6 mb-3">
                      <div className="chart-box" style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <div className="text-center mb-2">
                          <small style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>Total Ideas: {aggregateLevelChart.total}</small>
                        </div>
                        <div style={{ height: '320px', width: '100%' }}>
                          <ReactECharts 
                            option={aggregateLevelChart.option}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={true}
                            lazyUpdate={true}
                          />
                        </div>
                        <div className="text-center mt-2">
                          <button 
                            className="btn btn-sm" 
                            style={{ backgroundColor: '#F15c2a', color: '#fff', padding: '6px 20px', border: 'none', borderRadius: '4px', fontWeight: '500' }}
                            onClick={() => {
                              if (showLevelDetails) {
                                // Hide details
                                setShowProjectDetails(false);
                                setShowLevelDetails(false);
                              } else {
                                // Show level details only
                                setShowLevelDetails(true);
                                setShowProjectDetails(false);
                                setActiveTab('level');
                              }
                            }}
                          >
                            {showLevelDetails ? 'Hide Details' : 'View Details'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Project and Level Charts Section with Tabs */}
            {(projectChartData.length > 0 || levelChartData.length > 0) && (showProjectDetails || showLevelDetails) && (
              <div className="border rounded p-1 mb-1" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="chart-head mb-1">
                  <h5 style={{ background: 'none', color: '#333', margin: '0' }}>IDEA STATUS BREAKDOWN</h5>
                </div>
                
                {/* Tab Navigation */}
                <div className="mb-3" style={{ borderBottom: '2px solid #dee2e6' }}>
                  <button
                    onClick={() => {
                      setActiveTab('project');
                      setShowProjectDetails(true);
                      setShowLevelDetails(false);
                    }}
                    style={{ 
                      marginRight: '10px', 
                      borderRadius: '4px 4px 0 0',
                      border: 'none',
                      padding: '8px 20px',
                      background: activeTab === 'project' ? '#F15c2a' : 'transparent',
                      color: activeTab === 'project' ? '#fff' : '#6c757d',
                      textTransform: 'uppercase',
                      fontWeight: activeTab === 'project' ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    By Project
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('level');
                      setShowLevelDetails(true);
                      setShowProjectDetails(false);
                    }}
                    style={{ 
                      borderRadius: '4px 4px 0 0',
                      border: 'none',
                      padding: '8px 20px',
                      background: activeTab === 'level' ? '#F15c2a' : 'transparent',
                      color: activeTab === 'level' ? '#fff' : '#6c757d',
                      textTransform: 'uppercase',
                      fontWeight: activeTab === 'level' ? 'bold' : 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    By Level
                  </button>
                </div>
                
                {/* Tab Content */}
                <div className="row">
                  {(activeTab === 'project' && showProjectDetails) && projectChartData.map((chart, index) => (
                    <div key={index} className="col-md-4 mb-1">
                      <div className="chart-box" style={{ height: '320px', marginBottom: '1rem' }}>
                        <div style={{ height: '250px', width: '100%' }}>
                          <ReactECharts 
                            option={chart.option}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={true}
                            lazyUpdate={true}
                          />
                        </div>
                        <div className="text-center mt-1">
                          <h6 className="mb-1">{chart.name}</h6>
                          <small>Total Ideas: {chart.total}</small>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(activeTab === 'level' && showLevelDetails) && levelChartData.map((chart, index) => (
                    <div key={index} className="col-md-4 mb-1">
                      <div className="chart-box" style={{ height: '320px', marginBottom: '1rem' }}>
                        <div style={{ height: '250px', width: '100%' }}>
                          <ReactECharts 
                            option={chart.option}
                            style={{ height: '100%', width: '100%' }}
                            notMerge={true}
                            lazyUpdate={true}
                          />
                        </div>
                        <div className="text-center mt-1">
                          <h6 className="mb-1">{chart.name}</h6>
                          <small>Total Ideas: {chart.total}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className='row mb-3'>
              <div className='col-md-5'>
                <div className='table-search-box'>
                  <input 
                    type='text' 
                    placeholder='Search by title..' 
                    className='form-control' 
                    onChange={handleSearch} 
                    style={{ width: '50%' }}
                  />
                </div>
              </div>
              <div className='col-md-3'>
                <select 
                  className='form-control' 
                  value={selectedDepartment} 
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  <option value=''>All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
              <div className='col-md-2'>
                <select 
                  className='form-control' 
                  value={selectedStatus} 
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value=''>All Statuses</option>
                  {getUniqueStatuses().map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div className='col-md-2'>
                <button 
                  className='btn btn-secondary btn-sm w-100' 
                  onClick={clearFilters}
                  disabled={!selectedDepartment && !selectedStatus}
                >
                  CLEAR FILTERS
                </button>
              </div>
            </div>
            <DataTable
              columns={columns}
              data={data}
              pagination
              highlightOnHover
              responsive
              persistTableHead
              progressPending={loader}
              progressComponent={<Loader loading={loader} />}
            />
          </div>
        </div>
      </div>

      {/* View Modal */}
      {modalOpen && modalData && (
        <div className={styles.modalOverlay}>
          <div className={`${styles.modalCard} card p-0`}>
            <div className={`d-flex justify-content-between align-items-center ${styles.modalHeader}`}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <h5 className='m-0'>Idea Details</h5>
                <span className={`${styles.statusBadge} ${getStatusClass(modalData.status)}`}>{modalData.status || 'Unknown'}</span>
              </div>
              <button className={`btn btn-danger`} onClick={() => { setModalOpen(false); setModalData(null); }}>X</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSection}>
                <div className={styles.modalSectionTitle}>Submission Info</div>
                <div className='row'>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>ID:</strong> {modalData.id}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Title:</strong> {modalData.name || modalData.ideaProblem}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Submitted:</strong> {modalData.submitted || modalData.ideaDate || (modalData.meta && modalData.meta.idea_date)}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Submit Type:</strong> {modalData.submitType || (modalData.meta && modalData.meta.submitType)}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Employee ID:</strong> {modalData.empId || (modalData.meta && modalData.meta.empId)}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Email:</strong> {modalData.submitterEmail || (modalData.meta && modalData.meta.submitter && modalData.meta.submitter.email) || <span className={styles.modalTextMuted}>-</span>}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Phone:</strong> {modalData.submitterPhone || (modalData.meta && modalData.meta.submitter && modalData.meta.submitter.phone) || <span className={styles.modalTextMuted}>-</span>}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Department:</strong> {getDepartmentName(modalData.departmentId)}</div>
                  </div>
                  <div className='col-sm-6'>
                    <div className={styles.modalField}><strong>Assignee:</strong> {getUsername(modalData.assignee)}</div>
                  </div>
                  {(modalData.submitType === 'group' || (modalData.meta && modalData.meta.submitType === 'group')) && (modalData.members || (modalData.meta && modalData.meta.members)) && (
                    <div className='col-sm-12'>
                      <div className={styles.modalField}>
                        <strong>Group Members:</strong> 
                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {(modalData.members || (modalData.meta && modalData.meta.members) || []).map((member, index) => (
                            <span 
                              key={index}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: '#e3f2fd',
                                borderRadius: '12px',
                                fontSize: '13px',
                                color: '#1976d2'
                              }}
                            >
                              {member}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.modalSection}>
                <div className={styles.modalSectionTitle}>Idea Details</div>
                <div className={styles.modalField}><strong>Idea / Problem:</strong><br />{modalData.ideaProblem || (modalData.meta && modalData.meta.ideaProblem) || <span className={styles.modalTextMuted}>-</span>}</div>
                <div className={styles.modalField}><strong>Root Cause:</strong><br />{modalData.rootCause || (modalData.meta && modalData.meta.rootCause) || <span className={styles.modalTextMuted}>-</span>}</div>
                <div className={styles.modalField}><strong>Solution Proposed:</strong><br />{modalData.solutionProposed || modalData.ideaSolution || (modalData.meta && modalData.meta.solutionProposed) || <span className={styles.modalTextMuted}>-</span>}</div>
                <div className={styles.modalField}><strong>Benefits:</strong><br />{modalData.benefits || modalData.ideaBenefits || (modalData.meta && modalData.meta.benefits) || <span className={styles.modalTextMuted}>-</span>}</div>
                {modalData.rejectionComment && (
                  <div className={styles.modalField}><strong>Rejection comment:</strong><br />{modalData.rejectionComment}</div>
                )}
                {modalData.implementorComment && (
                  <div className={styles.modalField}><strong>Implementor Comment:</strong><br />{modalData.implementorComment}</div>
                )}

                <div className={styles.modalRow} style={{ marginTop: '0.5rem' }}>
                  <div style={{ flex: '1 1 300px' }}>
                    <div className={styles.modalField}><strong>Before Image:</strong></div>
                    {modalData.beforeImage ? (
                      typeof modalData.beforeImage === 'string' ? <img className={styles.modalImage} src={modalData.beforeImage.startsWith('http') ? modalData.beforeImage : 'https://d2c4d2pjttf9c7.cloudfront.net/' + modalData.beforeImage} alt='before' /> : <div className='text-muted'>{modalData.beforeImage.name || 'Uploaded file'}</div>
                    ) : <div className='text-muted'>No image</div>}
                  </div>
                  <div style={{ flex: '1 1 300px' }}>
                    <div className={styles.modalField}><strong>After Image:</strong></div>
                    {modalData.afterImage ? (
                      typeof modalData.afterImage === 'string' ? <img className={styles.modalImage} src={modalData.afterImage.startsWith('http') ? modalData.afterImage : 'https://d2c4d2pjttf9c7.cloudfront.net/' + modalData.afterImage} alt='after' /> : <div className='text-muted'>{modalData.afterImage.name || 'Uploaded file'}</div>
                    ) : <div className='text-muted'>No image</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Leader;

